import React, { useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

import { styles } from './WorkingHours.styles';
import WorkingHoursHeader from './components/WorkingHoursHeader';
import DayRowMobile from './components/DayRowMobile';
import { WorkingHoursProps } from './WorkingHoursContainer';
import { ReportSite } from 'store/storeTypes';

const WorkingHoursMobile = ({
  filterData,
  globalSystemGroup,
  selectedSystemGroup,
  selectedSystemGroupTypes,
  userSystemGroup,
  setSystemGroup,
  setSystemGroupTypes,
  selectedSite,
  selectedSites,
  setSites,
  applyMode,
  startDate,
  days,
  onSiteChange,
  onApplyModeChange,
  onStartDateChange,
  onToggleDay,
  onAddSlot,
  onUpdateSlot,
  onRemoveSlot,
  onRestoreDay,
  canEdit,
}: WorkingHoursProps) => {
  // Union of sites across every selected "Apply to system types" group — a site shared by
  // several groups (e.g. Apollo + DTG) should only be listed once, in the order first seen.
  const sites = useMemo(() => {
    const byId = new Map<string, ReportSite>();
    selectedSystemGroupTypes.forEach(group => {
      (filterData[group]?.sites ?? []).forEach(site => {
        if (site.id && !byId.has(site.id)) byId.set(site.id, site);
      });
    });
    return Array.from(byId.values());
  }, [filterData, selectedSystemGroupTypes]);

  useEffect(() => {
    // Sync local working-hours group with the globally selected system group on first mount.
    // Deps intentionally omitted — must run only once to avoid overwriting user's in-page selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (!selectedSystemGroup && globalSystemGroup) {
      setSystemGroup(globalSystemGroup);
    }
  }, []);

  useEffect(() => {
    // "Apply to system types" defaults to every type the user has access to, all selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (selectedSystemGroupTypes.length === 0 && userSystemGroup.length > 0) {
      setSystemGroupTypes(userSystemGroup);
    }
  }, [userSystemGroup]);

  useEffect(() => {
    // Re-pick a site whenever the available list changes and the current pick is stale.
    if (sites.some(site => site.id === selectedSite)) return;
    onSiteChange(sites[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites]);

  useEffect(() => {
    // "Apply change to" sites defaults to just the viewing site until the user checks more.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (selectedSites.length === 0 && selectedSite) {
      setSites([selectedSite]);
    }
  }, [selectedSite]);

  return (
    <Box sx={styles.mobilePage}>
      <WorkingHoursHeader
        sites={sites}
        selectedSite={selectedSite}
        onSiteChange={onSiteChange}
        selectedSites={selectedSites}
        setSites={setSites}
        applyMode={applyMode}
        onApplyModeChange={onApplyModeChange}
        startDate={startDate}
        onStartDateChange={onStartDateChange}
        userSystemGroup={userSystemGroup}
        selectedSystemGroupTypes={selectedSystemGroupTypes}
        setSystemGroupTypes={setSystemGroupTypes}
        readOnly={!canEdit}
      />

      <Box sx={styles.mobileDaysList}>
        {days.map((day, index) => (
          <React.Fragment key={day.id}>
            <DayRowMobile
              day={day}
              selectedSite={selectedSite}
              onToggleDay={onToggleDay}
              onAddSlot={onAddSlot}
              onUpdateSlot={onUpdateSlot}
              onRemoveSlot={onRemoveSlot}
              onRestoreDay={onRestoreDay}
              readOnly={!canEdit}
            />
            {index !== days.length - 1 && <Divider sx={styles.dayDivider} />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default WorkingHoursMobile;
